import React, { useState, useEffect } from 'react';
import { View, ScrollView, StyleSheet, Dimensions } from 'react-native';
import {
  Card,
  Text,
  Button,
  Chip,
  Surface,
  useTheme,
  ProgressBar,
  TextInput,
  SegmentedButtons,
  RadioButton,
  Checkbox,
  Switch,
  Divider,
  List,
  Avatar,
  Badge,
} from 'react-native-paper';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useNavigation } from '@react-navigation/native';
import {
  Brain,
  Coffee,
  Target,
  Clock,
  BookOpen,
  Users,
  Zap,
  CheckCircle,
  ArrowRight,
  Sparkles,
  TrendingUp,
  BarChart3,
} from 'lucide-react-native';

interface OnboardingStep {
  id: string;
  title: string;
  description: string;
  type: 'assessment' | 'preferences' | 'goals' | 'personality' | 'ai-analysis';
  isCompleted: boolean;
  isActive: boolean;
  estimatedTime: number;
  aiInsights?: string[];
}

interface UserProfile {
  name: string;
  experience: 'beginner' | 'intermediate' | 'advanced' | 'expert';
  learningStyle: 'visual' | 'auditory' | 'kinesthetic' | 'reading';
  timeAvailability: number;
  goals: string[];
  equipment: string[];
  preferences: Record<string, any>;
}

interface AIInsight {
  type: 'recommendation' | 'warning' | 'suggestion' | 'optimization';
  message: string;
  confidence: number;
  reasoning: string;
  impact: 'high' | 'medium' | 'low';
}

const { width } = Dimensions.get('window');

export function AIOnboardingScreen() {
  const theme = useTheme();
  const insets = useSafeAreaInsets();
  const navigation = useNavigation();

  const [currentStep, setCurrentStep] = useState(0);
  const [userProfile, setUserProfile] = useState<UserProfile>({
    name: '',
    experience: 'beginner',
    learningStyle: 'visual',
    timeAvailability: 5,
    goals: [],
    equipment: [],
    preferences: {},
  });

  const [aiInsights, setAiInsights] = useState<AIInsight[]>([]);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [showAIRecommendations, setShowAIRecommendations] = useState(false);

  const onboardingSteps: OnboardingStep[] = [
    {
      id: 'welcome',
      title: 'Welcome to CoffeePlexity',
      description:
        "Let's get to know you and create your personalized coffee journey",
      type: 'assessment',
      isCompleted: false,
      isActive: true,
      estimatedTime: 2,
    },
    {
      id: 'experience',
      title: 'Coffee Experience Assessment',
      description: 'Tell us about your current coffee knowledge and skills',
      type: 'assessment',
      isCompleted: false,
      isActive: false,
      estimatedTime: 3,
    },
    {
      id: 'learning-style',
      title: 'Learning Style Discovery',
      description: 'Discover how you learn best with our AI-powered assessment',
      type: 'personality',
      isCompleted: false,
      isActive: false,
      estimatedTime: 4,
    },
    {
      id: 'goals',
      title: 'Goal Setting & Planning',
      description: 'Define your coffee learning objectives and timeline',
      type: 'goals',
      isCompleted: false,
      isActive: false,
      estimatedTime: 5,
    },
    {
      id: 'equipment',
      title: 'Equipment & Resources',
      description: 'Assess your current setup and identify needs',
      type: 'preferences',
      isCompleted: false,
      isActive: false,
      estimatedTime: 3,
    },
    {
      id: 'ai-analysis',
      title: 'AI Journey Creation',
      description: 'Our AI creates your personalized learning path',
      type: 'ai-analysis',
      isCompleted: false,
      isActive: false,
      estimatedTime: 2,
    },
  ];

  const experienceLevels = [
    {
      value: 'beginner',
      label: 'Beginner',
      description: 'New to coffee brewing',
    },
    {
      value: 'intermediate',
      label: 'Intermediate',
      description: 'Some brewing experience',
    },
    {
      value: 'advanced',
      label: 'Advanced',
      description: 'Experienced home barista',
    },
    {
      value: 'expert',
      label: 'Expert',
      description: 'Professional or enthusiast',
    },
  ];

  const learningStyles = [
    {
      value: 'visual',
      label: 'Visual',
      description: 'Learn through images and videos',
    },
    {
      value: 'auditory',
      label: 'Auditory',
      description: 'Learn through listening and discussion',
    },
    {
      value: 'kinesthetic',
      label: 'Hands-on',
      description: 'Learn through practice and doing',
    },
    {
      value: 'reading',
      label: 'Reading',
      description: 'Learn through text and documentation',
    },
  ];

  const commonGoals = [
    'Master espresso brewing',
    'Learn pour-over techniques',
    'Understand coffee origins',
    'Improve latte art',
    'Learn cupping and tasting',
    'Master different brewing methods',
    'Understand coffee chemistry',
    'Learn about coffee equipment',
  ];

  const commonEquipment = [
    'Espresso machine',
    'Grinder',
    'Pour-over setup',
    'French press',
    'AeroPress',
    'Moka pot',
    'Scale',
    'Thermometer',
    'Timer',
    'Filters',
  ];

  const handleNextStep = () => {
    if (currentStep < onboardingSteps.length - 1) {
      setCurrentStep(currentStep + 1);
      // Mark current step as completed
      onboardingSteps[currentStep].isCompleted = true;
      onboardingSteps[currentStep].isActive = false;
      onboardingSteps[currentStep + 1].isActive = true;
    }
  };

  const handlePreviousStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleExperienceChange = (experience: UserProfile['experience']) => {
    setUserProfile(prev => ({ ...prev, experience }));
  };

  const handleLearningStyleChange = (
    learningStyle: UserProfile['learningStyle']
  ) => {
    setUserProfile(prev => ({ ...prev, learningStyle }));
  };

  const handleGoalToggle = (goal: string) => {
    setUserProfile(prev => ({
      ...prev,
      goals: prev.goals.includes(goal)
        ? prev.goals.filter(g => g !== goal)
        : [...prev.goals, goal],
    }));
  };

  const handleEquipmentToggle = (equipment: string) => {
    setUserProfile(prev => ({
      ...prev,
      equipment: prev.equipment.includes(equipment)
        ? prev.equipment.filter(e => e !== equipment)
        : [...prev.equipment, equipment],
    }));
  };

  const simulateAIAnalysis = async () => {
    setIsAnalyzing(true);

    // Simulate AI processing time
    await new Promise(resolve => setTimeout(resolve, 3000));

    // Generate mock AI insights
    const insights: AIInsight[] = [
      {
        type: 'recommendation',
        message: 'Start with pour-over brewing to build fundamental skills',
        confidence: 0.92,
        reasoning:
          'Based on your beginner level and visual learning preference',
        impact: 'high',
      },
      {
        type: 'optimization',
        message: 'Focus on 2-3 goals initially for better retention',
        confidence: 0.87,
        reasoning: 'You selected 6 goals, which may lead to scattered focus',
        impact: 'medium',
      },
      {
        type: 'suggestion',
        message: 'Consider investing in a quality grinder early',
        confidence: 0.94,
        reasoning: 'Grind consistency is crucial for all brewing methods',
        impact: 'high',
      },
      {
        type: 'warning',
        message:
          'Your time availability may be optimistic for advanced techniques',
        confidence: 0.78,
        reasoning:
          '5 hours/week is sufficient for basics but may limit advanced practice',
        impact: 'medium',
      },
    ];

    setAiInsights(insights);
    setIsAnalyzing(false);
    setShowAIRecommendations(true);
  };

  const getStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <View style={styles.stepContent}>
            <View style={styles.welcomeHeader}>
              <Brain size={64} color={theme.colors.primary} />
              <Text variant='headlineMedium' style={styles.welcomeTitle}>
                Welcome to CoffeePlexity
              </Text>
              <Text variant='bodyLarge' style={styles.welcomeSubtitle}>
                Your AI-powered coffee learning journey starts here
              </Text>
            </View>

            <Surface style={styles.featureCard} elevation={1}>
              <View style={styles.featureHeader}>
                <Sparkles size={24} color={theme.colors.primary} />
                <Text variant='titleMedium' style={styles.featureTitle}>
                  AI-Powered Personalization
                </Text>
              </View>
              <Text variant='bodyMedium' style={styles.featureDescription}>
                Our advanced AI analyzes your preferences, learning style, and
                goals to create a perfectly tailored coffee education
                experience.
              </Text>
            </Surface>

            <Surface style={styles.featureCard} elevation={1}>
              <View style={styles.featureHeader}>
                <TrendingUp size={24} color={theme.colors.secondary} />
                <Text variant='titleMedium' style={styles.featureTitle}>
                  Adaptive Learning Path
                </Text>
              </View>
              <Text variant='bodyMedium' style={styles.featureDescription}>
                Your journey automatically adjusts based on your progress,
                ensuring optimal learning efficiency and engagement.
              </Text>
            </Surface>

            <Surface style={styles.featureCard} elevation={1}>
              <View style={styles.featureHeader}>
                <BarChart3 size={24} color={theme.colors.tertiary} />
                <Text variant='titleMedium' style={styles.featureTitle}>
                  Real-time Analytics
                </Text>
              </View>
              <Text variant='bodyMedium' style={styles.featureDescription}>
                Track your progress with detailed insights and recommendations
                powered by machine learning.
              </Text>
            </Surface>
          </View>
        );

      case 1:
        return (
          <View style={styles.stepContent}>
            <Text variant='titleLarge' style={styles.stepTitle}>
              What's your coffee experience level?
            </Text>
            <Text variant='bodyMedium' style={styles.stepDescription}>
              This helps us tailor content to your current knowledge
            </Text>

            <View style={styles.optionsContainer}>
              {experienceLevels.map(level => (
                <Card
                  key={level.value}
                  style={[
                    styles.optionCard,
                    userProfile.experience === level.value &&
                      styles.selectedOptionCard,
                  ]}
                  onPress={() =>
                    handleExperienceChange(
                      level.value as UserProfile['experience']
                    )
                  }
                >
                  <Card.Content>
                    <View style={styles.optionContent}>
                      <RadioButton
                        value={level.value}
                        status={
                          userProfile.experience === level.value
                            ? 'checked'
                            : 'unchecked'
                        }
                        onPress={() =>
                          handleExperienceChange(
                            level.value as UserProfile['experience']
                          )
                        }
                      />
                      <View style={styles.optionText}>
                        <Text variant='titleMedium' style={styles.optionTitle}>
                          {level.label}
                        </Text>
                        <Text
                          variant='bodySmall'
                          style={styles.optionDescription}
                        >
                          {level.description}
                        </Text>
                      </View>
                    </View>
                  </Card.Content>
                </Card>
              ))}
            </View>
          </View>
        );

      case 2:
        return (
          <View style={styles.stepContent}>
            <Text variant='titleLarge' style={styles.stepTitle}>
              How do you learn best?
            </Text>
            <Text variant='bodyMedium' style={styles.stepDescription}>
              Our AI will optimize content delivery for your learning style
            </Text>

            <View style={styles.optionsContainer}>
              {learningStyles.map(style => (
                <Card
                  key={style.value}
                  style={[
                    styles.optionCard,
                    userProfile.learningStyle === style.value &&
                      styles.selectedOptionCard,
                  ]}
                  onPress={() =>
                    handleLearningStyleChange(
                      style.value as UserProfile['learningStyle']
                    )
                  }
                >
                  <Card.Content>
                    <View style={styles.optionContent}>
                      <RadioButton
                        value={style.value}
                        status={
                          userProfile.learningStyle === style.value
                            ? 'checked'
                            : 'unchecked'
                        }
                        onPress={() =>
                          handleLearningStyleChange(
                            style.value as UserProfile['learningStyle']
                          )
                        }
                      />
                      <View style={styles.optionText}>
                        <Text variant='titleMedium' style={styles.optionTitle}>
                          {style.label}
                        </Text>
                        <Text
                          variant='bodySmall'
                          style={styles.optionDescription}
                        >
                          {style.description}
                        </Text>
                      </View>
                    </View>
                  </Card.Content>
                </Card>
              ))}
            </View>
          </View>
        );

      case 3:
        return (
          <View style={styles.stepContent}>
            <Text variant='titleLarge' style={styles.stepTitle}>
              What are your coffee goals?
            </Text>
            <Text variant='bodyMedium' style={styles.stepDescription}>
              Select all that apply - we'll prioritize based on your experience
              level
            </Text>

            <View style={styles.goalsContainer}>
              {commonGoals.map(goal => (
                <Checkbox.Item
                  key={goal}
                  label={goal}
                  status={
                    userProfile.goals.includes(goal) ? 'checked' : 'unchecked'
                  }
                  onPress={() => handleGoalToggle(goal)}
                  style={styles.goalItem}
                />
              ))}
            </View>

            <View style={styles.timeContainer}>
              <Text variant='titleMedium' style={styles.timeTitle}>
                How much time can you dedicate per week?
              </Text>
              <Text variant='bodyMedium' style={styles.timeValue}>
                {userProfile.timeAvailability} hours
              </Text>
              <TextInput
                mode='outlined'
                label='Hours per week'
                value={userProfile.timeAvailability.toString()}
                onChangeText={text => {
                  const value = parseInt(text) || 1;
                  setUserProfile(prev => ({
                    ...prev,
                    timeAvailability: Math.max(1, Math.min(20, value)),
                  }));
                }}
                keyboardType='numeric'
                style={styles.textInput}
              />
              <View style={styles.timeLabels}>
                <Text variant='bodySmall'>1h</Text>
                <Text variant='bodySmall'>20h</Text>
              </View>
            </View>
          </View>
        );

      case 4:
        return (
          <View style={styles.stepContent}>
            <Text variant='titleLarge' style={styles.stepTitle}>
              What equipment do you have?
            </Text>
            <Text variant='bodyMedium' style={styles.stepDescription}>
              This helps us recommend appropriate content and identify any gaps
            </Text>

            <View style={styles.equipmentContainer}>
              {commonEquipment.map(item => (
                <Checkbox.Item
                  key={item}
                  label={item}
                  status={
                    userProfile.equipment.includes(item)
                      ? 'checked'
                      : 'unchecked'
                  }
                  onPress={() => handleEquipmentToggle(item)}
                  style={styles.equipmentItem}
                />
              ))}
            </View>
          </View>
        );

      case 5:
        return (
          <View style={styles.stepContent}>
            <Text variant='titleLarge' style={styles.stepTitle}>
              AI Journey Creation
            </Text>
            <Text variant='bodyMedium' style={styles.stepDescription}>
              Our AI is analyzing your profile to create the perfect learning
              path
            </Text>

            {!showAIRecommendations ? (
              <View style={styles.aiAnalysisContainer}>
                <Surface style={styles.analysisCard} elevation={2}>
                  <View style={styles.analysisHeader}>
                    <Brain size={32} color={theme.colors.primary} />
                    <Text variant='titleMedium' style={styles.analysisTitle}>
                      AI Analysis in Progress
                    </Text>
                  </View>

                  {isAnalyzing ? (
                    <View style={styles.analyzingContainer}>
                      <Text variant='bodyMedium' style={styles.analyzingText}>
                        Analyzing your profile...
                      </Text>
                      <ProgressBar
                        progress={0.7}
                        color={theme.colors.primary}
                        style={styles.progressBar}
                        indeterminate
                      />
                      <Text variant='bodySmall' style={styles.analyzingSubtext}>
                        Processing learning patterns and preferences
                      </Text>
                    </View>
                  ) : (
                    <Button
                      mode='contained'
                      onPress={simulateAIAnalysis}
                      style={styles.analyzeButton}
                      icon='brain'
                    >
                      Start AI Analysis
                    </Button>
                  )}
                </Surface>
              </View>
            ) : (
              <View style={styles.recommendationsContainer}>
                <Surface style={styles.recommendationsCard} elevation={2}>
                  <View style={styles.recommendationsHeader}>
                    <CheckCircle size={32} color={theme.colors.primary} />
                    <Text
                      variant='titleLarge'
                      style={styles.recommendationsTitle}
                    >
                      Your AI Recommendations
                    </Text>
                  </View>

                  <View style={styles.insightsContainer}>
                    {aiInsights.map((insight, index) => (
                      <View key={index} style={styles.insightItem}>
                        <View style={styles.insightHeader}>
                          <Chip
                            mode='outlined'
                            textStyle={{ color: getInsightColor(insight.type) }}
                            style={{
                              borderColor: getInsightColor(insight.type),
                            }}
                          >
                            {insight.type}
                          </Chip>
                          <Badge style={styles.confidenceBadge}>
                            {Math.round(insight.confidence * 100)}%
                          </Badge>
                        </View>
                        <Text
                          variant='bodyMedium'
                          style={styles.insightMessage}
                        >
                          {insight.message}
                        </Text>
                        <Text
                          variant='bodySmall'
                          style={styles.insightReasoning}
                        >
                          {insight.reasoning}
                        </Text>
                        <Chip
                          mode='outlined'
                          textStyle={{ color: getImpactColor(insight.impact) }}
                          style={{
                            borderColor: getImpactColor(insight.impact),
                          }}
                          compact
                        >
                          {insight.impact} impact
                        </Chip>
                      </View>
                    ))}
                  </View>

                  <Button
                    mode='contained'
                    onPress={() =>
                      (navigation as any).navigate('aiJourneyFlow')
                    }
                    style={styles.createJourneyButton}
                    icon='map'
                  >
                    Create My Journey
                  </Button>
                </Surface>
              </View>
            )}
          </View>
        );

      default:
        return null;
    }
  };

  const getInsightColor = (type: AIInsight['type']) => {
    switch (type) {
      case 'recommendation':
        return theme.colors.primary;
      case 'warning':
        return theme.colors.error;
      case 'suggestion':
        return theme.colors.secondary;
      case 'optimization':
        return theme.colors.tertiary;
      default:
        return theme.colors.outline;
    }
  };

  const getImpactColor = (impact: AIInsight['impact']) => {
    switch (impact) {
      case 'high':
        return theme.colors.primary;
      case 'medium':
        return theme.colors.secondary;
      case 'low':
        return theme.colors.outline;
      default:
        return theme.colors.outline;
    }
  };

  const progress = (currentStep + 1) / onboardingSteps.length;

  return (
    <View
      style={[styles.container, { backgroundColor: theme.colors.background }]}
    >
      {/* Progress Header */}
      <Surface style={styles.progressHeader} elevation={2}>
        <View style={styles.progressInfo}>
          <Text variant='titleMedium' style={styles.progressTitle}>
            Step {currentStep + 1} of {onboardingSteps.length}
          </Text>
          <Text variant='bodySmall' style={styles.progressSubtitle}>
            {onboardingSteps[currentStep].title}
          </Text>
        </View>
        <ProgressBar
          progress={progress}
          color={theme.colors.primary}
          style={styles.progressBar}
        />
      </Surface>

      {/* Step Content */}
      <ScrollView
        style={styles.content}
        contentContainerStyle={styles.contentContainer}
      >
        {getStepContent()}
      </ScrollView>

      {/* Navigation Footer */}
      <Surface style={styles.footer} elevation={4}>
        <View style={styles.footerContent}>
          {currentStep > 0 && (
            <Button
              mode='outlined'
              onPress={handlePreviousStep}
              style={styles.footerButton}
            >
              Previous
            </Button>
          )}

          <View style={styles.footerSpacer} />

          {currentStep < onboardingSteps.length - 1 ? (
            <Button
              mode='contained'
              onPress={handleNextStep}
              style={styles.footerButton}
              disabled={!canProceed()}
            >
              Next
            </Button>
          ) : (
            <Button
              mode='contained'
              onPress={() => (navigation as any).navigate('aiJourneyFlow')}
              style={styles.footerButton}
              disabled={!showAIRecommendations}
              icon='map'
            >
              Create Journey
            </Button>
          )}
        </View>
      </Surface>
    </View>
  );

  function canProceed(): boolean {
    switch (currentStep) {
      case 0: // Welcome
        return true;
      case 1: // Experience
        return userProfile.experience !== '';
      case 2: // Learning Style
        return userProfile.learningStyle !== '';
      case 3: // Goals
        return userProfile.goals.length > 0 && userProfile.timeAvailability > 0;
      case 4: // Equipment
        return true; // Equipment is optional
      case 5: // AI Analysis
        return showAIRecommendations;
      default:
        return false;
    }
  }
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  progressHeader: {
    padding: 16,
    backgroundColor: theme => theme.colors.surface,
  },
  progressInfo: {
    marginBottom: 12,
  },
  progressTitle: {
    fontWeight: 'bold',
    marginBottom: 2,
  },
  progressSubtitle: {
    opacity: 0.7,
  },
  progressBar: {
    height: 8,
    borderRadius: 4,
  },
  content: {
    flex: 1,
  },
  contentContainer: {
    padding: 16,
    paddingBottom: 100,
  },
  stepContent: {
    marginBottom: 24,
  },
  stepTitle: {
    fontWeight: 'bold',
    marginBottom: 8,
    textAlign: 'center',
  },
  stepDescription: {
    textAlign: 'center',
    marginBottom: 24,
    opacity: 0.7,
  },
  welcomeHeader: {
    alignItems: 'center',
    marginBottom: 32,
  },
  welcomeTitle: {
    marginTop: 16,
    marginBottom: 8,
    textAlign: 'center',
    fontWeight: 'bold',
  },
  welcomeSubtitle: {
    textAlign: 'center',
    opacity: 0.7,
  },
  featureCard: {
    padding: 16,
    marginBottom: 16,
    borderRadius: 12,
  },
  featureHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  featureTitle: {
    marginLeft: 12,
    fontWeight: 'bold',
  },
  featureDescription: {
    opacity: 0.8,
    lineHeight: 20,
  },
  optionsContainer: {
    gap: 12,
  },
  optionCard: {
    marginBottom: 12,
    borderRadius: 12,
  },
  selectedOptionCard: {
    borderColor: theme => theme.colors.primary,
    borderWidth: 2,
  },
  optionContent: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  optionText: {
    marginLeft: 12,
    flex: 1,
  },
  optionTitle: {
    fontWeight: 'bold',
    marginBottom: 2,
  },
  optionDescription: {
    opacity: 0.7,
  },
  goalsContainer: {
    marginBottom: 24,
  },
  goalItem: {
    paddingVertical: 8,
  },
  timeContainer: {
    marginBottom: 24,
  },
  timeTitle: {
    fontWeight: 'bold',
    marginBottom: 16,
    textAlign: 'center',
  },
  timeValue: {
    textAlign: 'center',
    fontSize: 24,
    fontWeight: 'bold',
    marginBottom: 16,
    color: theme => theme.colors.primary,
  },
  slider: {
    marginBottom: 8,
  },
  textInput: {
    marginBottom: 8,
  },
  timeLabels: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  equipmentContainer: {
    marginBottom: 24,
  },
  equipmentItem: {
    paddingVertical: 8,
  },
  aiAnalysisContainer: {
    alignItems: 'center',
  },
  analysisCard: {
    padding: 24,
    borderRadius: 16,
    width: '100%',
    maxWidth: 400,
  },
  analysisHeader: {
    alignItems: 'center',
    marginBottom: 24,
  },
  analysisTitle: {
    marginTop: 12,
    fontWeight: 'bold',
  },
  analyzingContainer: {
    alignItems: 'center',
  },
  analyzingText: {
    marginBottom: 16,
    fontWeight: 'bold',
  },
  progressBar: {
    width: '100%',
    height: 8,
    borderRadius: 4,
    marginBottom: 16,
  },
  analyzingSubtext: {
    opacity: 0.7,
    textAlign: 'center',
  },
  analyzeButton: {
    marginTop: 16,
  },
  recommendationsContainer: {
    alignItems: 'center',
  },
  recommendationsCard: {
    padding: 24,
    borderRadius: 16,
    width: '100%',
    maxWidth: 500,
  },
  recommendationsHeader: {
    alignItems: 'center',
    marginBottom: 24,
  },
  recommendationsTitle: {
    marginTop: 12,
    fontWeight: 'bold',
  },
  insightsContainer: {
    marginBottom: 24,
  },
  insightItem: {
    marginBottom: 20,
    padding: 16,
    backgroundColor: '#f8f9fa',
    borderRadius: 12,
  },
  insightHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  confidenceBadge: {
    backgroundColor: theme => theme.colors.primary,
  },
  insightMessage: {
    fontWeight: 'bold',
    marginBottom: 8,
  },
  insightReasoning: {
    opacity: 0.7,
    marginBottom: 12,
    fontStyle: 'italic',
  },
  createJourneyButton: {
    marginTop: 16,
  },
  footer: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: theme => theme.colors.surface,
  },
  footerContent: {
    flexDirection: 'row',
    padding: 16,
    alignItems: 'center',
  },
  footerButton: {
    minWidth: 120,
  },
  footerSpacer: {
    flex: 1,
  },
});
